import React, { useState, useMemo, useEffect, useCallback, useContext } from 'react'
import Selector from '../_control/OptionsSelector'
import BasicMeasurementPicker from '../_control/BasicMeasurementPicker'
import FieldSectionSelector from '../_control/FieldSectionSelector'
import { css } from '@emotion/react'
import { getUnixTime } from 'date-fns'
import Orientation from '../test/Orientation'
import useRawPromise from '../../hooks/useRawPromise'
import Pitch from '../../context/Pitch'
import FormLoader from './FormLoader'
import useBridge from '../../hooks/useBridge'
import EventDateTimePicker from '../_control/EventDateTimePicker'
import { FormattedMessage, useIntl } from 'react-intl'
import { zonesToSectionState } from '../../utils/eventUtils'
import EventFormSubmit from './EventFormSubmit'
import RequiredMark from './RequiredMark'
import LightingZonePicker from '../events/LightingZonePicker'


export default function LightingForm({
    eventType,
    event,
    onPostRequest,
}) {
    //utils
    const { post, put, get } = useRawPromise()
    const { formatMessage } = useIntl()
    //field state
    const [selectedDateTime, setSelectedDateTime] = useState()
    const [lightingType, setLightingType] = useState()
    const [gridSize, setGridSize] = useState()

    const [zoneResults, setZoneResults] = useState([])

    //other
    const { data: gridSizeOptions, loading: waitingForGridSizeOptions } = useBridge(
        `/api/current/frontend-bridge/artificial-lighting-grid-sizes`
    )
    const { data: lightingTypeOptions, loading: waitingForLightingTypeOptions } = useBridge(
        `/api/current/frontend-bridge/artificial-lighting-types`
    )

    const { data: pitch } = useContext(Pitch)

    const [isLoading, setIsLoading] = useState(!!event)
    const [formError, setFormError] = useState()

    useEffect(() => {
        if (event) {
            async function getEventAndHydrateFormData(eventId) {
                setIsLoading(true)
                const { data } = await get(`/api/current/artificial-lighting-events/${eventId}`)
                setSelectedDateTime(new Date(data.timestamp * 1000))
                setLightingType(data.lightType)
                setGridSize(data.gridSize)
                setZoneResults(data.zoneResults.toSorted((a,b) => {
                    if (a.posY !== b.posY) {
                        return a.posY - b.posY
                    } else {
                        return a.posX - b.posX
                    }
                }))
                setIsLoading(false)
            }
            getEventAndHydrateFormData(event.id)
        }
    }, [event])

    const validateAndSubmit = useCallback(async () => {
        /* TO DO: submission and validation */
        if (!lightingType) return
        if (!gridSize) return

        setFormError()
        setIsLoading(true)

        const eventPayload = {
            lightType: lightingType,
            gridSize,
            timestamp: getUnixTime(selectedDateTime),
        }

        if (event) {
            const { data: putResponse, error } = await put(`${event['@id']}`, { body: eventPayload })

            if (error) {
                setIsLoading(false)
                onPostRequest(false, error)
            }
            else if (putResponse) {
                const { data: lightingData } = await get(`/api/current/artificial-lighting-events/${putResponse.id}`);

                lightingData.zoneResults.forEach(async zone => {
                    const timeTaken = zoneResults.find(z => (z.posX === zone.posX) && (z.posY === zone.posY))?.timeTaken ?? null
                    await put(`${zone['@id']}`, {
                        body: {
                            active: !!timeTaken ? 1 : 0,
                            timeTaken: timeTaken,
                        },
                    })
                })

                lightingData.zoneResults = zoneResults
                onPostRequest(true, lightingData)
            }
        } else {
            const newEventPayLoad = {
                ...eventPayload,
                name: `artificial lighting event for pitch ${pitch['id']}`,
                pitch: pitch['@id'],
            }

            const { data: postResponse, error } = await post(`/api/current/${eventType.path}`, { body: newEventPayLoad })

            if (error) {
                setIsLoading(false)
                onPostRequest(false, error)
            }
            else if (postResponse) {
                const { data: lightingData } = await get(`/api/current/artificial-lighting-events/${postResponse.id}`);
                lightingData.zoneResults.forEach(async zone => {
                    const timeTaken = zoneResults.find(z => (z.posX === zone.posX) && (z.posY === zone.posY))?.timeTaken ?? null
                    await put(`${zone['@id']}`, {
                        body: {
                            active: !!timeTaken ? 1 : 0,
                            timeTaken: timeTaken,
                        },
                    })
                })

                lightingData.zoneResults = zoneResults
                onPostRequest(true, lightingData)
            }
        }
    }, [event, pitch, lightingType, gridSize, zoneResults, selectedDateTime])

    const grid = useMemo(() => gridSize?.split('-') || [0, 0], [gridSize])

    const isBusy = useMemo(() => (
        waitingForGridSizeOptions || waitingForLightingTypeOptions || isLoading
    ), [waitingForGridSizeOptions, waitingForLightingTypeOptions, isLoading])

    const isValid = useMemo(() => {
        return !!(gridSize && lightingType && zoneResults.some(zone => !!zone.timeTaken))
    }, [gridSize, lightingType, zoneResults])

    return (<>
        {isBusy ? <FormLoader /> : <>
            <div css={css`display: flex; flex-direction: column;`}>
                <EventDateTimePicker
                    unixDate={selectedDateTime}
                    setUnixDate={setSelectedDateTime}
                />
                
                <Selector
                    label={formatMessage({id: 'artificialLighting'})}
                    optionsObject={lightingTypeOptions}
                    state={lightingType}
                    setState={setLightingType}
                    isRequired={true}
                />

                <Selector
                    label={formatMessage({id: 'grid'})}
                    optionsObject={gridSizeOptions}
                    state={gridSize}
                    setState={setGridSize}
                    isRequired={true}
                />

                <>
                    <h3 css={{ marginTop: 8, marginBottom: 8 }} >
                        <FormattedMessage id='locations' />
                    </h3>
                    <LightingZonePicker
                        backgroundType={pitch?.pitchBackground || pitch?.club?.sport}
                        grid={grid}
                        orientation={pitch.orientation}
                        zoneResults={zoneResults}
                        setZoneResults={setZoneResults}
                    />
                </>
            </div>
        </>}

        <EventFormSubmit
            onSubmit={validateAndSubmit}
            loading={isBusy}
            disabled={!isValid}
            errorMsg={formError}
        />
    </>)
}

const style = {
    compass: css`
        position: relative;
        height: 2em;
        width: 2em;
        bottom: -1em;
        left: calc(50% - 1em);
        background-color: rgba(44,52,58);
        box-shadow: none;
        margin-bottom:40px;
    `,
}